import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  BarSeries,
  LineSeries,
  BollingerSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  MouseCoordinateX,
  MouseCoordinateY,
  EdgeIndicator,
  OHLCTooltip,
  BollingerBandTooltip,
  MovingAverageTooltip,
  discontinuousTimeScaleProviderBuilder,
  GenericChartComponent,
  getAxisCanvas,
  mouseBasedZoomAnchor,
  ema,
  sma,
  bollingerBand,
} from 'react-financial-charts';
import { timeFormat } from 'd3-time-format';
import { Layers, Calendar, Settings2 } from 'lucide-react';
import { useOHLCData, timeframeConfig, intervalConfig } from '../charts/useOHLCData';
import { colors, chartMargin, priceFormat } from '../charts/chartTheme';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../UI/dropdown-menu';

const VISIBLE_CANDLES = 80;
const VOLUME_HEIGHT = 50;
const MIN_BARS = 10;
const TARGET_BARS = 200;
const MAX_BARS = 1000;

// ── Timeframe / interval compatibility ──

function getValidIntervals(timeframeKey) {
  const tf = timeframeConfig[timeframeKey];
  if (!tf) return Object.keys(intervalConfig);
  const totalMinutes = tf.days * 24 * 60;
  return Object.entries(intervalConfig)
    .filter(([, cfg]) => totalMinutes / cfg.minutes >= MIN_BARS)
    .map(([key]) => key);
}

function getDefaultInterval(timeframeKey) {
  const valid = getValidIntervals(timeframeKey);
  if (valid.length === 0) return '5m';
  const tf = timeframeConfig[timeframeKey];
  if (!tf) return valid[0];
  const totalMinutes = tf.days * 24 * 60;
  let best = valid[0];
  let bestDiff = Infinity;
  for (const key of valid) {
    const bars = Math.min(totalMinutes / intervalConfig[key].minutes, MAX_BARS);
    const diff = Math.abs(bars - TARGET_BARS);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = key;
    }
  }
  return best;
}

function computeBoundsFromData(data) {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.high));
  const min = Math.min(...data.map((d) => d.low));
  const range = max - min;
  const offset = range * 0.2;
  return {
    upper: parseFloat((max - offset).toFixed(2)),
    lower: parseFloat((min + offset).toFixed(2)),
  };
}

// ── Canvas overlay components ──

const BoundLine = memo(({ yValue, stroke, label }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      const y = Math.round(yScale(yValue));
      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      if (label) {
        ctx.setLineDash([]);
        ctx.fillStyle = stroke;
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText(label, 5, y - 6);
      }
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
));

const BoundsFill = memo(({ upperBound, lowerBound }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      const yU = Math.round(yScale(upperBound));
      const yL = Math.round(yScale(lowerBound));
      ctx.save();
      ctx.fillStyle = 'rgba(59, 130, 246, 0.06)';
      ctx.fillRect(0, Math.min(yU, yL), width, Math.abs(yL - yU));
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
));

const GridLevels = memo(({ upperBound, lowerBound, levels }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      const step = (upperBound - lowerBound) / (levels + 1);
      ctx.save();
      ctx.strokeStyle = colors.gridLevel;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 6]);
      for (let i = 1; i <= levels; i++) {
        const price = lowerBound + step * i;
        const y = Math.round(yScale(price));
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
));

// ── ChartInner — memoized child that owns the ResizeObserver ──

const ChartInner = memo(function ChartInner({
  containerRef,
  data,
  xScale,
  xAccessor,
  displayXAccessor,
  xExtents,
  symbol,
  loading,
  showVolume,
  showEma20,
  showEma50,
  showSma200,
  showBB,
  showBounds,
  showGrid,
  ema20,
  ema50,
  sma200,
  bb,
  upperBound,
  lowerBound,
  gridLevels,
  hasBounds,
  hasGrid,
  chartSettings = {},
  maTooltipOptions,
}) {
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const observerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const w = Math.floor(rect.width);
        const h = Math.floor(rect.height);
        setDims((prev) => {
          if (prev.width === w && prev.height === h) return prev;
          return { width: w, height: h };
        });
      }
    };

    measure();

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    });
    observer.observe(node);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  const h = dims.height || 450;
  const volumeH = showVolume ? VOLUME_HEIGHT : 0;
  const effectiveBottomMargin = showVolume ? chartMargin.bottom : 24;
  const priceChartHeight = h - volumeH - chartMargin.top - effectiveBottomMargin;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No price data available for {symbol}</p>
      </div>
    );
  }

  if (dims.width <= 0 || priceChartHeight <= 0) return null;

  return (
    <ChartCanvas
      width={dims.width}
      height={h}
      data={data}
      xScale={xScale}
      xAccessor={xAccessor}
      displayXAccessor={displayXAccessor}
      xExtents={xExtents}
      margin={{ ...chartMargin, bottom: effectiveBottomMargin }}
      seriesName={`${symbol}-builder`}
      ratio={window.devicePixelRatio || 1}
      mouseMoveEvent={true}
      panEvent={chartSettings.pan !== false}
      zoomEvent={chartSettings.zoom !== false}
      clamp={chartSettings.clamp ? 'both' : 'left'}
      zoomAnchor={mouseBasedZoomAnchor}
    >
      {/* Main price chart */}
      <Chart
        id={1}
        yExtents={(d) => {
          const vals = [];
          if (isFinite(d.high)) vals.push(d.high);
          if (isFinite(d.low)) vals.push(d.low);
          if (showBB && d.bb) {
            if (isFinite(d.bb.top)) vals.push(d.bb.top);
            if (isFinite(d.bb.bottom)) vals.push(d.bb.bottom);
          }
          return vals.length ? vals : [0];
        }}
        height={priceChartHeight}
        padding={{ top: 10, bottom: 10 }}
      >
        <XAxis
          strokeStyle={colors.grid}
          tickStrokeStyle={colors.axis}
          tickLabelFill={colors.axis}
          fontSize={11}
        />
        <YAxis
          strokeStyle={colors.grid}
          tickStrokeStyle={colors.axis}
          tickLabelFill={colors.axis}
          tickFormat={priceFormat}
          fontSize={11}
        />

        <CandlestickSeries
          fill={(d) => (d.close >= d.open ? colors.candleUpFill : colors.candleDownFill)}
          wickStroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          stroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
        />

        {/* Overlays */}
        {showEma20 && <LineSeries yAccessor={d => d.ema20 ?? undefined} strokeStyle="#f59e0b" strokeWidth={1.5} />}
        {showEma50 && <LineSeries yAccessor={d => d.ema50 ?? undefined} strokeStyle="#8b5cf6" strokeWidth={1.5} />}
        {showSma200 && <LineSeries yAccessor={d => d.sma200 ?? undefined} strokeStyle="#ec4899" strokeWidth={1.5} />}
        {showBB && (
          <BollingerSeries
            yAccessor={d => d.bb}
            strokeStyle={{ top: '#64748b', middle: '#64748b', bottom: '#64748b' }}
            fillStyle="rgba(100, 116, 139, 0.05)"
          />
        )}

        <MouseCoordinateX displayFormat={timeFormat('%b %d %H:%M')} />
        <MouseCoordinateY rectWidth={60} displayFormat={priceFormat} />

        <EdgeIndicator
          itemType="last"
          orient="right"
          edgeAt="right"
          yAccessor={(d) => d.close}
          fill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          displayFormat={priceFormat}
        />

        <OHLCTooltip
          origin={(w) => [w - 220, 16]}
          textFill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          labelFill={colors.tooltipLabel}
        />

        {maTooltipOptions.length > 0 && (
          <MovingAverageTooltip
            origin={(w) => [w - 220, 32]}
            options={maTooltipOptions}
          />
        )}

        {showBB && (
          <BollingerBandTooltip
            origin={(w) => [w - 220, 32 + maTooltipOptions.length * 16]}
            yAccessor={d => d.bb}
            options={bb.options()}
          />
        )}

        {/* Builder-specific: bounds and grid */}
        {showBounds && upperBound && lowerBound && (
          <BoundsFill upperBound={Number(upperBound)} lowerBound={Number(lowerBound)} />
        )}
        {showGrid && hasGrid && (
          <GridLevels upperBound={Number(upperBound)} lowerBound={Number(lowerBound)} levels={gridLevels} />
        )}
        {showBounds && upperBound && (
          <BoundLine yValue={Number(upperBound)} stroke={colors.boundUpper} label={`Upper: $${Number(upperBound).toFixed(2)}`} />
        )}
        {showBounds && lowerBound && (
          <BoundLine yValue={Number(lowerBound)} stroke={colors.boundLower} label={`Lower: $${Number(lowerBound).toFixed(2)}`} />
        )}
      </Chart>

      {/* Volume sub-chart */}
      {showVolume && (
        <Chart id={2} yExtents={(d) => isFinite(d.volume) ? d.volume : 0} height={volumeH} origin={(w, h) => [0, h - volumeH]}>
          <BarSeries
            yAccessor={(d) => d.volume}
            fillStyle={(d) => d.close >= d.open ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}
          />
        </Chart>
      )}

      <CrossHairCursor strokeStyle={chartSettings.crosshair === false ? 'transparent' : colors.crosshair} />
    </ChartCanvas>
  );
});

ChartInner.displayName = 'ChartInner';

// ── Overlay options ──

const OVERLAY_OPTIONS = [
  { value: 'ema20', label: 'EMA 20' },
  { value: 'ema50', label: 'EMA 50' },
  { value: 'sma200', label: 'SMA 200' },
  { value: 'bb', label: 'Bollinger' },
];

// ── Main chart component ──

const CandlestickChart = memo(({
  symbol,
  symbolName,
  upperBound,
  lowerBound,
  gridLevels,
  timeframe: initialTimeframe = '1M',
  onBoundsChange,
  isMobile = false,
}) => {
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [barInterval, setBarInterval] = useState(() => getDefaultInterval(initialTimeframe));
  const [showEma20, setShowEma20] = useState(true);
  const [showEma50, setShowEma50] = useState(false);
  const [showSma200, setShowSma200] = useState(false);
  const [showBB, setShowBB] = useState(false);
  const [showBounds, setShowBounds] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const [chartSettings, setChartSettings] = useState({
    zoom: true,
    pan: true,
    clamp: true,
    crosshair: true,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const containerRef = useRef(null);
  const boundsInitialized = useRef(false);
  const prevSymbol = useRef(symbol);

  const { data: rawData, loading, currentPrice } = useOHLCData(symbol, barInterval, timeframe);

  // Indicator calculators
  const ema20 = useMemo(() => ema().id('ema20').options({ windowSize: 20 }).merge((d, c) => { d.ema20 = c; }).accessor(d => d.ema20), []);
  const ema50 = useMemo(() => ema().id('ema50').options({ windowSize: 50 }).merge((d, c) => { d.ema50 = c; }).accessor(d => d.ema50), []);
  const sma200 = useMemo(() => sma().id('sma200').options({ windowSize: 200 }).merge((d, c) => { d.sma200 = c; }).accessor(d => d.sma200), []);
  const bb = useMemo(() => bollingerBand().merge((d, c) => { d.bb = c; }).accessor(d => d.bb), []);

  // Mobile overlay multi-select
  const selectedOverlays = useMemo(() => {
    const sel = [];
    if (showEma20) sel.push('ema20');
    if (showEma50) sel.push('ema50');
    if (showSma200) sel.push('sma200');
    if (showBB) sel.push('bb');
    return sel;
  }, [showEma20, showEma50, showSma200, showBB]);

  const handleOverlayChange = useCallback((next) => {
    setShowEma20(next.includes('ema20'));
    setShowEma50(next.includes('ema50'));
    setShowSma200(next.includes('sma200'));
    setShowBB(next.includes('bb'));
  }, []);

  // Compute percent change over visible timeframe
  const pctChange = useMemo(() => {
    if (!rawData.length || !currentPrice) return null;
    const openPrice = rawData[0].open;
    if (!openPrice) return null;
    return ((currentPrice - openPrice) / openPrice) * 100;
  }, [rawData, currentPrice]);

  // Auto-clamp interval when timeframe changes
  const validIntervals = useMemo(() => getValidIntervals(timeframe), [timeframe]);

  useEffect(() => {
    if (!validIntervals.includes(barInterval)) {
      setBarInterval(getDefaultInterval(timeframe));
    }
  }, [timeframe, validIntervals, barInterval]);

  useEffect(() => {
    if (prevSymbol.current !== symbol) {
      boundsInitialized.current = false;
      prevSymbol.current = symbol;
    }
  }, [symbol]);

  useEffect(() => {
    if (!rawData.length || !onBoundsChange || boundsInitialized.current) return;
    if (upperBound && lowerBound) {
      boundsInitialized.current = true;
      return;
    }
    const visibleSlice = rawData.slice(Math.max(0, rawData.length - VISIBLE_CANDLES));
    const bounds = computeBoundsFromData(visibleSlice);
    if (bounds) {
      onBoundsChange(bounds);
      boundsInitialized.current = true;
    }
  }, [rawData, onBoundsChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute indicators using library calculators
  const enrichedData = useMemo(() => {
    if (!rawData.length) return [];
    let merged = rawData.map((d) => ({ ...d }));
    merged = ema20(ema50(sma200(bb(merged))));
    return merged;
  }, [rawData, ema20, ema50, sma200, bb]);

  const ScaleProvider = useMemo(
    () => discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date),
    [],
  );

  const { data, xScale, xAccessor, displayXAccessor } = useMemo(() => {
    if (!enrichedData.length) return { data: [], xScale: null, xAccessor: null, displayXAccessor: null };
    return ScaleProvider(enrichedData);
  }, [enrichedData, ScaleProvider]);

  const xExtents = useMemo(() => {
    if (!data.length) return [0, 0];
    const start = xAccessor(data[Math.max(0, data.length - VISIBLE_CANDLES)]);
    const end = xAccessor(data[data.length - 1]) + 3;
    return [start, end];
  }, [data, xAccessor]);

  // Moving average tooltip options
  const maTooltipOptions = useMemo(() => {
    const opts = [];
    if (showEma20) opts.push({ yAccessor: ema20.accessor(), type: 'EMA', stroke: '#f59e0b', windowSize: 20 });
    if (showEma50) opts.push({ yAccessor: ema50.accessor(), type: 'EMA', stroke: '#8b5cf6', windowSize: 50 });
    if (showSma200) opts.push({ yAccessor: sma200.accessor(), type: 'SMA', stroke: '#ec4899', windowSize: 200 });
    return opts;
  }, [showEma20, showEma50, showSma200, ema20, ema50, sma200]);

  const hasBounds = !!(onBoundsChange && upperBound && lowerBound);
  const hasGrid = !!(hasBounds && gridLevels && gridLevels > 0);

  if (!symbol) {
    return (
      <div className="flex items-center justify-center bg-card border border-border rounded-xl h-full">
        <p className="text-muted-foreground">Select a symbol to view price chart</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-3 select-none h-full flex flex-col">
      {/* ── Header: symbol info + chart settings gear ── */}
      <div className="mb-2">
        <div className="flex justify-between items-start gap-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h3 className="text-lg font-bold text-foreground">{symbol}</h3>
              {currentPrice != null && (
                <span className="text-lg font-semibold text-foreground">${currentPrice.toFixed(2)}</span>
              )}
              {pctChange != null && (
                <span className={`text-sm font-semibold ${pctChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%
                  <span className="text-muted-foreground font-normal ml-1">{timeframe}</span>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-0.5">
              {symbolName && <p className="text-muted-foreground">{symbolName}</p>}
              {showBounds && lowerBound && upperBound && (
                <p className="text-muted-foreground">
                  Bounds:{' '}
                  <span className="text-green-500 font-semibold">${Number(lowerBound).toFixed(2)}</span>
                  {' – '}
                  <span className="text-red-500 font-semibold">${Number(upperBound).toFixed(2)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Chart settings gear — inline with ticker */}
          {!isMobile && (
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setSettingsOpen(!settingsOpen)}
                className={`p-1.5 rounded transition-colors ${
                  settingsOpen ? 'bg-primary/20 text-primary' : 'text-muted-foreground/50 hover:text-foreground hover:bg-white/5'
                }`}
                title="Chart Settings"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </button>
              {settingsOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl p-3 min-w-[160px]">
                  <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">Chart Behavior</p>
                  {[
                    { key: 'zoom', label: 'Zoom' },
                    { key: 'pan', label: 'Pan' },
                    { key: 'clamp', label: 'Clamp Edges' },
                    { key: 'crosshair', label: 'Crosshair' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center justify-between py-1.5 cursor-pointer group">
                      <span className="text-xs text-foreground group-hover:text-primary transition-colors">{label}</span>
                      <button
                        type="button"
                        onClick={() => setChartSettings((s) => ({ ...s, [key]: !s[key] }))}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          chartSettings[key] ? 'bg-primary' : 'bg-muted-foreground/30'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          chartSettings[key] ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`} />
                      </button>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Chart canvas ── */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 relative"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{ touchAction: 'none', WebkitUserDrag: 'none' }}
      >
        <ChartInner
          containerRef={containerRef}
          data={data}
          xScale={xScale}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xExtents={xExtents}
          symbol={symbol}
          loading={loading}
          showVolume={isMobile ? false : showVolume}
          showEma20={showEma20}
          showEma50={showEma50}
          showSma200={showSma200}
          showBB={showBB}
          showBounds={showBounds}
          showGrid={showGrid}
          ema20={ema20}
          ema50={ema50}
          sma200={sma200}
          bb={bb}
          upperBound={upperBound}
          lowerBound={lowerBound}
          gridLevels={gridLevels}
          hasBounds={hasBounds}
          hasGrid={hasGrid}
          chartSettings={chartSettings}
          maTooltipOptions={maTooltipOptions}
        />
      </div>

      {/* ── Footer: interval + timeframe selectors ── */}
      {isMobile ? (
        <div className="mt-1.5 pt-1.5 border-t border-border flex items-center justify-between gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              >
                <Calendar className="h-3.5 w-3.5" />
                {intervalConfig[barInterval]?.label || barInterval} / {timeframe}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Interval</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={barInterval} onValueChange={setBarInterval}>
                {validIntervals.map((key) => (
                  <DropdownMenuRadioItem key={key} value={key}>{intervalConfig[key].label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Timeframe</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={timeframe} onValueChange={setTimeframe}>
                {Object.keys(timeframeConfig).map((tf) => (
                  <DropdownMenuRadioItem key={tf} value={tf}>{tf}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <MultiSelectDropdown
            label="Overlays"
            icon={Layers}
            options={[
              ...OVERLAY_OPTIONS,
              ...(hasBounds ? [{ value: 'bounds', label: 'Bounds' }] : []),
              ...(hasGrid ? [{ value: 'grid', label: 'Grid' }] : []),
            ]}
            selected={[...selectedOverlays, ...(showBounds ? ['bounds'] : []), ...(showGrid ? ['grid'] : [])]}
            onChange={(next) => {
              handleOverlayChange(next);
              setShowBounds(next.includes('bounds'));
              setShowGrid(next.includes('grid'));
            }}
          />
        </div>
      ) : (
        <div className="mt-1.5 pt-1.5 border-t border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <select
              value={barInterval}
              onChange={(e) => setBarInterval(e.target.value)}
              className="px-2.5 py-1 text-xs bg-card border border-border text-foreground rounded focus:outline-none focus:ring-1 focus-visible:ring-ring"
            >
              {validIntervals.map((key) => (
                <option key={key} value={key}>{intervalConfig[key].label}</option>
              ))}
            </select>

            <MultiSelectDropdown
              label="Overlays"
              icon={Layers}
              options={[
                ...OVERLAY_OPTIONS,
                { value: 'volume', label: 'Volume' },
                ...(hasBounds ? [{ value: 'bounds', label: 'Bounds' }] : []),
                ...(hasGrid ? [{ value: 'grid', label: 'Grid' }] : []),
              ]}
              selected={[
                ...selectedOverlays,
                ...(showVolume ? ['volume'] : []),
                ...(showBounds ? ['bounds'] : []),
                ...(showGrid ? ['grid'] : []),
              ]}
              onChange={(next) => {
                handleOverlayChange(next);
                setShowVolume(next.includes('volume'));
                setShowBounds(next.includes('bounds'));
                setShowGrid(next.includes('grid'));
              }}
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {Object.keys(timeframeConfig).map((tf) => (
              <button
                type="button"
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  timeframe === tf
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-card text-muted-foreground hover:text-foreground hover:bg-white/5 border border-border'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

CandlestickChart.displayName = 'CandlestickChart';

export default CandlestickChart;
